import React from "react";

import { Button, Col, Input, Row, Select, message } from "antd";

import { _getEnterpriseUomReferences } from "../../services";

/**
 * Turns editor rows into the `punchoutUomMapping` payload.
 * Returns { mapping } or { error } - never both.
 */
export const buildUomMapping = (rows) => {
    const mapping = {};
    for (const row of rows) {
        const eezeeCode = (row.eezeeCode || "").trim();
        const buyerCode = (row.buyerCode || "").trim();
        if (!eezeeCode || !buyerCode) {
            return { error: "Every row needs both an eezee code and a buyer code." };
        }
        if (Object.prototype.hasOwnProperty.call(mapping, eezeeCode)) {
            return { error: `Duplicate eezee code: ${eezeeCode}` };
        }
        mapping[eezeeCode] = buyerCode;
    }
    return { mapping };
};

/**
 * Dropdown options for the eezee-code side: active IST codes, plus any stored
 * key missing from the list so an existing map stays editable if IST
 * deactivates a code.
 */
export const buildKeyOptions = (references, storedKeys) => {
    const options = (references || [])
        .filter(ref => ref.status === "ACTIVE" && ref.uomCode)
        .map(ref => ({ value: ref.uomCode, label: ref.description ? `${ref.uomCode} — ${ref.description}` : ref.uomCode }));
    const known = new Set(options.map(o => o.value));
    const extras = [...new Set(storedKeys || [])].filter(key => key && !known.has(key)).map(key => ({ value: key, label: key }));
    return [...options, ...extras];
};

const toRows = (value) => Object.keys(value || {}).map(eezeeCode => ({ eezeeCode, buyerCode: value[eezeeCode] }));

const hasEmptySide = (rows) => rows.some(row => !(row.eezeeCode || "").trim() || !(row.buyerCode || "").trim());

class EnterpriseUomMapping extends React.Component {

    state = { rows: toRows(this.props.value), saving: false, references: null };

    scrollBox = React.createRef();

    async componentDidMount() {
        try {
            const { data } = await _getEnterpriseUomReferences();
            if (!Array.isArray(data)) throw new Error("uom-references: expected an array");
            this.setState({ references: data });
        } catch (e) {
            console.error("Failed to load UOM references", e);
            message.warning("Could not load the eezee UOM list; eezee codes are free text.");
        }
    }

    // The parent refetches the enterprise wholesale; follow the server value
    // when it changes so a Save cannot write back a stale map.
    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value) {
            this.setState({ rows: toRows(this.props.value) });
        }
    }

    updateRow = (index, field, fieldValue) => {
        this.setState({
            rows: this.state.rows.map((row, i) => i === index ? { ...row, [field]: fieldValue } : row)
        });
    };

    addRow = () => this.setState(
        { rows: [...this.state.rows, { eezeeCode: "", buyerCode: "" }] },
        () => {
            const box = this.scrollBox.current;
            if (box) box.scrollTop = box.scrollHeight;
        }
    );

    removeRow = (index) => this.setState({ rows: this.state.rows.filter((row, i) => i !== index) });

    save = async () => {
        const { mapping, error } = buildUomMapping(this.state.rows);
        if (error) {
            message.error(error);
            return;
        }
        this.setState({ saving: true });
        try {
            await this.props.onSave(mapping);
            this.setState({ rows: toRows(mapping) });
            message.success("Punchout UOM mapping saved");
        } catch (e) {
            message.error("Failed to save Punchout UOM mapping");
        } finally {
            this.setState({ saving: false });
        }
    };

    render() {
        const { rows, saving, references } = this.state;
        const keyOptions = references && buildKeyOptions(references, rows.map(row => row.eezeeCode));
        return (
            <div>
                <h4>Punchout UOM Mapping</h4>
                <p style={{ color: "rgba(0,0,0,0.45)" }}>
                    Only list codes that differ. Unlisted codes are sent as-is; products with no code send EA.
                </p>
                <div ref={this.scrollBox} style={{ maxHeight: 360, overflowY: "auto", paddingRight: 8, marginBottom: 8 }}>
                    {rows.map((row, index) => (
                        <Row gutter={8} style={{ marginBottom: 8 }} type="flex" key={index}>
                            <Col span={10}>
                                {keyOptions ? (
                                    <Select
                                        showSearch
                                        style={{ width: "100%" }}
                                        placeholder="eezee code (e.g. BX)"
                                        value={row.eezeeCode || undefined}
                                        optionFilterProp="children"
                                        onChange={v => this.updateRow(index, "eezeeCode", v)}>
                                        {keyOptions.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
                                    </Select>
                                ) : (
                                    <Input
                                        placeholder="eezee code (e.g. BX)"
                                        value={row.eezeeCode}
                                        onChange={e => this.updateRow(index, "eezeeCode", e.target.value)} />
                                )}
                            </Col>
                            <Col span={10}>
                                <Input
                                    placeholder="buyer code (e.g. BOX)"
                                    value={row.buyerCode}
                                    onChange={e => this.updateRow(index, "buyerCode", e.target.value)} />
                            </Col>
                            <Col span={4}>
                                <Button icon="delete" onClick={() => this.removeRow(index)} />
                            </Col>
                        </Row>
                    ))}
                </div>
                <Row gutter={8} type="flex">
                    <Col>
                        <Button icon="plus" onClick={this.addRow}>Add row</Button>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            loading={saving}
                            disabled={hasEmptySide(rows)}
                            onClick={this.save}>Save mapping</Button>
                    </Col>
                </Row>
            </div>
        );
    }
}

export default EnterpriseUomMapping;



// WEBPACK FOOTER //
// ./src/components/enterprise/enterprise-uom-mapping.js